import { Component, Input, OnDestroy, OnInit, TemplateRef, ViewChild } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { catchError, concat, debounceTime, distinctUntilChanged, Observable, of, Subject, Subscription, switchMap, tap } from 'rxjs';
import { AssessmentService } from 'src/app/services/assessment.service';
import { AuthService } from 'src/app/services/auth.service';
import { ProjectService } from 'src/app/services/project.service';

@Component({
    selector: 'app-view-assessment-team',
    templateUrl: './view-assessment-team.component.html',
    styleUrl: './view-assessment-team.component.scss',
    standalone: false
})
export class ViewAssessmentTeamComponent implements OnInit, OnDestroy {
  @ViewChild('addMemberModal') addMemberModal!: TemplateRef<any>;
  @ViewChild('successModal') successModal!: TemplateRef<any>;
  @ViewChild('removeMemberModal') removeMemberModal!: TemplateRef<any>;

  @Input() currentAssessment!: any;

  addMemberModalRef: any;
	successModalRef: any;
  removeMemberModalRef: any;

  private usersSearchSubscription?: Subscription;
  private searchSubject = new Subject<string>();
  private addUserSearchSubject = new Subject<string>();
  assessmentID!: number;
  projectId!: number;

  members: any[] = [];

  loading = false;
  searchText: string = '';
  
  success = false;
  message = '';
  title = '';

  currentUser: any = null;
	current_client: any = null;

  isOwner = false;
  colspan = 0;

  currentPage: number = 1;
  pageSize: number = 7;
  totalMembers: number = 0;
  sortColumn: string = 'createdAt';
  sortDirection: string = 'desc';

  startIndex: number = 0;
  endIndex: number = 0;

  addMemberForm!: FormGroup;
  submitted = false;
  spinner = false;

  users: any[] = [];
  usersLoading = false;
  usersInput$ = new Subject<string>();

  isEditMode = false;
  selectedMemberName = '';
  selectedMember: any = null;

  allCaseRoles: any[] = [
		{ code: 'EDITOR', name: 'Editor', disabled: false },
		{ code: 'ANALYST', name: 'Analyst', disabled: false },
		{ code: 'OWNER', name: 'Project Owner', disabled: false }
	];

  displayableRoles: any[] = [
		{ code: 'EDITOR', name: 'Editor', disabled: false },
		{ code: 'ANALYST', name: 'Analyst', disabled: false },
		{ code: 'OWNER', name: 'Project Owner', disabled: false }
	];

  private rolesSub?: Subscription;

  constructor(private fb: FormBuilder, private projectService: ProjectService, private assessmentService: AssessmentService, private authService: AuthService, private modalService: NgbModal) {}
  
  ngOnInit(): void {
    this.loading = true;
    this.projectService.setLeftSideBar(true);

    this.currentUser = this.authService.currentUserValue;
    this.current_client = this.authService.currentClientValue;
    this.isOwner = this.current_client.id === this.currentAssessment.case_owner_id;

    this.colspan = this.isOwner ? 6 : 5;

    this.assessmentID = this.currentAssessment.id;
    this.projectId = this.currentAssessment.project_id;
    this.getDetails();

    this.searchSubject.pipe(
      debounceTime(400),
      distinctUntilChanged()
    ).subscribe(() => {
      this.currentPage = 1;
      this.getDetails();
    });
  }

  getDetails() {
    this.loading = true;
    const params = {
      page: this.currentPage.toString(),
      pageSize: this.pageSize.toString(),
      searchText: this.searchText,
      sortColumn: this.sortColumn,
      sortDirection: this.sortDirection
    };

    this.assessmentService.getProjectCaseMembers(this.projectId, this.assessmentID, params).subscribe({
      next: (response) => {
        this.members = response.members || [];
        this.totalMembers = response.totalMembers;
        this.calculateIndexes();

        this.loading = false;
      },
      error: (err) => {
        this.authService.toastErrorMessage(err.message ? err.message : err, 'Fetching Error');
      }
    });
  }
  
  ngOnDestroy(): void {
    this.rolesSub?.unsubscribe();
    this.usersSearchSubscription?.unsubscribe();
  }

  calculateIndexes() {
    this.startIndex = (this.currentPage - 1) * this.pageSize + 1;
    this.endIndex = Math.min(this.currentPage * this.pageSize, this.totalMembers);
  }

  onSearch() {
    this.searchSubject.next(this.searchText);
  }

  openAddEditMember(member: any) {
    this.isEditMode = false;
    this.submitted = false;
    this.spinner = false;

    this.addMemberForm = this.fb.group({
      client_user_id: [null, Validators.required],
      roles: [[], Validators.required]
    });

    this.displayableRoles = this.allCaseRoles;
    
    this.getUserList();
    this.setupUsersSearch();
    this.setupRoleLogic();

    if (member) {
      this.selectedMember = member;
      this.isEditMode = true;
      this.selectedMemberName = `${member.user_details.first_name} ${member.user_details.last_name}`;
      
      this.addMemberForm.patchValue({
        client_user_id: member.client_user_id,
        roles: member.roles
      });

      this.addMemberForm.get('client_user_id')?.disable();
    }

    this.addMemberModalRef = this.modalService.open(this.addMemberModal, { size: 'l', backdrop: 'static', keyboard : false });
  }

  onSearchUser(event: { term: string }): void {
    this.addUserSearchSubject.next(event.term);
  }

  customSearchFn(term: string, item: any) {
    return true;
  }

  private setupUsersSearch(): void {
    this.usersSearchSubscription = this.addUserSearchSubject.pipe(
      debounceTime(400),
      distinctUntilChanged()
    ).subscribe(searchText => {
      this.getUserList(searchText || '');
    });
  }

  getUserList(searchText = ''): void {
    this.usersLoading = true;
    this.assessmentService.getProjectMembers(this.projectId, { searchText }).subscribe({
      next: (response) => {
        this.users = response;
        this.usersLoading = false;
      },
      error: (err) => {
        this.users = [];
        this.authService.toastErrorMessage(err.message, 'Failed to fetch users list!');
        this.usersLoading = false;
      }
    });
  }

  private setupRoleLogic(): void {
    this.rolesSub?.unsubscribe();
    const rolesControl = this.addMemberForm.get('roles');
    
    this.rolesSub = rolesControl?.valueChanges.subscribe((selectedRoles: string[]) => {
      if (!selectedRoles) {
        this.updateDisplayableRoles();
        return;
      }

      const hasOwner = selectedRoles.includes('OWNER');
      
      if (hasOwner && selectedRoles.length > 1) {
        rolesControl.patchValue(['OWNER'], { emitEvent: false });
      }

      this.updateDisplayableRoles();
    });
  }

  private updateDisplayableRoles(): void {
    const selectedRoles = this.addMemberForm.get('roles')?.value || [];
    const isOwnerSelected = selectedRoles.includes('OWNER');
    
    this.displayableRoles = this.allCaseRoles.map(role => {
      let isDisabled = false;
      
      if (isOwnerSelected) {
        isDisabled = role.code !== 'OWNER';
      } else if (selectedRoles.length > 0) {
        isDisabled = role.code === 'OWNER';
      }
      
      return {
        ...role,
        disabled: isDisabled
      };
    });
  }

  isRoleDisabled(roleCode: string): boolean {
    const selectedRoles = this.addMemberForm.get('roles')?.value || [];
    if (roleCode === 'OWNER') {
      return selectedRoles.length > 0 && !selectedRoles.includes('OWNER');
    }
    return selectedRoles.includes('OWNER');
  }

  onSubmit() {
    this.submitted = true;
    if (this.addMemberForm.invalid) return;
    
    this.spinner = true;
    const formData = this.addMemberForm.value;

    if (this.isEditMode) {
      formData.client_user_id = this.selectedMember.client_user_id;
    }

    this.title = this.isEditMode ? 'Update member' : 'Adding new member';

    this.assessmentService.addOrUpdateCaseMember(this.projectId, this.assessmentID, formData).subscribe({
      next: (res: any) => {
        this.spinner = false;
        this.submitted = false;
        this.success = true;
        this.message = res.message;
        this.addMemberModalRef.close();
        this.successModalRef = this.modalService.open(this.successModal, { size: 'l', backdrop: 'static', keyboard : false });
      },
      error: (err: any) => {
        this.spinner = false;
        this.submitted = false;
        this.success = false;
        this.message = err.error?.message || err.message;
        this.addMemberModalRef.close();
        this.successModalRef = this.modalService.open(this.successModal, { size: 'l', backdrop: 'static', keyboard : false });
      }
    });
  }

  openRemoveMemberModal(member: any) {
    this.submitted = false;
    this.spinner = false;
    this.selectedMember = member;
    this.selectedMemberName = `${member.user_details.first_name} ${member.user_details.last_name}`;
		this.removeMemberModalRef = this.modalService.open(this.removeMemberModal, { size: 'l', backdrop: 'static', keyboard : false });
	}

  removeMember() {
    this.submitted = true;
    this.spinner = true;

    this.assessmentService.removeCaseMember(this.projectId, this.assessmentID, this.selectedMember.case_member_id).subscribe({
      next: (res: any) => {
        this.spinner = false;
        this.submitted = false;
        this.success = true;
        this.message = res.message;
        this.removeMemberModalRef.close();
        this.successModalRef = this.modalService.open(this.successModal, { size: 'l', backdrop: 'static', keyboard : false });
      },
      error: (err: any) => {
        this.spinner = false;
        this.submitted = false;
        this.success = false;
        this.message = err.error?.message || err.message;
        this.removeMemberModalRef.close();
        this.successModalRef = this.modalService.open(this.successModal, { size: 'l', backdrop: 'static', keyboard : false });
      }
    });
  }

  closeModal() {
    if (this.success) {
      this.getDetails();
    }

    this.modalService.dismissAll();
	}
}
